import EditIcon from "@mui/icons-material/Edit";
import { Button, Chip, Grid, Stack } from "@mui/material";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";

import {
  formatFlashPoint,
  formatPhRange,
  prettyPrintCharacterizationBasis,
  prettyPrintPackingGroup,
  prettyPrintPhysicalState,
} from "./profileDisplay";
import { ProfileSection } from "./ProfileSection";
import { WasteProfileSectionData, WasteStreamProfileData } from "./types";

/**
 * Field widths chosen so a field here is about as wide as one in the drawer's
 * Details card, which is two-up inside the narrow left column. These sections
 * are full width, so four-up lands at the same field width rather than
 * stretching values across the whole drawer.
 */
const FIELD = { xs: 12, sm: 6, lg: 3 } as const;
const WIDE = { xs: 12, lg: 6 } as const;

/**
 * An unset value renders blank under its label, matching the Details card
 * directly above (an unset waste code shows a label and nothing else). An em
 * dash would be louder than the drawer's own convention for the same state.
 * `DisplayOnlyField` keeps the row height, so blanks do not collapse the grid.
 */
const orBlank = (value?: string | null): string | undefined => value || undefined;

const formatNumber = (value?: number | null): string | undefined =>
  value == null ? undefined : `${value}`;

/**
 * Hazard characteristics are data, not status, so they read as a neutral
 * outlined tag list. The drawer deliberately reserves color for meaning
 * (see DiscrepanciesSection's note on not giving color two jobs on one
 * screen), and `ProvisionalWasteStreamChip` sets the small/outlined shape
 * every chip in this drawer follows.
 */
const HazardChips = ({ profile }: { profile: WasteProfileSectionData | null }) => {
  const hazards = [
    { label: "Ignitable", on: profile?.isIgnitable },
    { label: "Corrosive", on: profile?.isCorrosive },
    { label: "Reactive", on: profile?.isReactive },
    { label: "Toxic", on: profile?.isToxic },
  ].filter((h) => h.on);

  if (hazards.length === 0) return null;

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {hazards.map((h) => (
        <Chip key={h.label} size="small" variant="outlined" label={h.label} />
      ))}
    </Stack>
  );
};

export const CharacterizationSection = ({
  wasteStream,
  onEdit,
}: {
  wasteStream: WasteStreamProfileData;
  onEdit: () => void;
}) => {
  const profile = wasteStream.profile ?? null;
  const basis = prettyPrintCharacterizationBasis(profile?.basis);

  return (
    <ProfileSection
      title="Characterization"
      action={
        <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>
          Edit characterization
        </Button>
      }
      adornment={
        basis ? <Chip size="small" variant="outlined" label={`Basis: ${basis}`} /> : undefined
      }
    >
      <Stack spacing={2}>
        <HazardChips profile={profile} />
        <Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
          <Grid size={FIELD}>
            <DisplayOnlyField
              label="Physical state"
              text={orBlank(prettyPrintPhysicalState(profile?.physicalState))}
              copyable
            />
          </Grid>
          <Grid size={FIELD}>
            <DisplayOnlyField
              label="pH range"
              text={orBlank(formatPhRange(profile?.phLow, profile?.phHigh))}
              copyable
            />
          </Grid>
          <Grid size={FIELD}>
            <DisplayOnlyField
              label="Flash point"
              text={orBlank(
                formatFlashPoint(
                  profile?.flashPointF,
                  profile?.flashPointMinF,
                  profile?.flashPointMaxF,
                  profile?.flashPointQualifier
                )
              )}
              copyable
            />
          </Grid>
          <Grid size={FIELD}>
            <DisplayOnlyField
              label="Specific gravity"
              text={formatNumber(profile?.specificGravity)}
              copyable
            />
          </Grid>
          <Grid size={WIDE}>
            <DisplayOnlyField
              label="Physical description"
              text={orBlank(profile?.physicalDescription)}
              copyable
              wrapValue
            />
          </Grid>
          <Grid size={WIDE}>
            <DisplayOnlyField
              label="Generating process"
              text={orBlank(profile?.generatingProcess)}
              copyable
              wrapValue
            />
          </Grid>
          {/*
            The codes the characterization claims, which is a different question
            from the stream's own federalWasteCodes — those aggregate what the
            manifests reported.
          */}
          <Grid size={FIELD}>
            <DisplayOnlyField
              label="EPA waste codes"
              text={orBlank(profile?.federalWasteCodes?.join(", "))}
              copyable
            />
          </Grid>
          <Grid size={FIELD}>
            <DisplayOnlyField
              label="State waste codes"
              text={orBlank(profile?.stateWasteCodes?.join(", "))}
              copyable
            />
          </Grid>
        </Grid>

        {/*
          The DOT block lives on WasteStream, not WasteProfile, but it is
          characterization data the customer owns and edits in the same form,
          so it reads as part of this section rather than a fifth one.
        */}
        <Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
          <Grid size={WIDE}>
            <DisplayOnlyField
              label="DOT proper shipping name"
              text={orBlank(wasteStream.dotProperShippingName)}
              copyable
              wrapValue
            />
          </Grid>
          <Grid size={FIELD}>
            <DisplayOnlyField label="UN/NA ID" text={orBlank(wasteStream.dotIdNumber)} copyable />
          </Grid>
          <Grid size={FIELD}>
            <DisplayOnlyField
              label="DOT hazard class"
              text={orBlank(wasteStream.dotHazardClass)}
              copyable
            />
          </Grid>
          <Grid size={FIELD}>
            <DisplayOnlyField
              label="Packing group"
              text={orBlank(prettyPrintPackingGroup(wasteStream.dotPackingGroup))}
              copyable
            />
          </Grid>
          <Grid size={FIELD}>
            <DisplayOnlyField
              label="DOT hazardous"
              text={wasteStream.dotHazardous ? "Yes" : "No"}
              copyable={false}
            />
          </Grid>
        </Grid>
      </Stack>
    </ProfileSection>
  );
};
